import React, { useContext, useState, useEffect, useRef, MouseEventHandler } from 'react';
import { Col, Row, Form, Button, Container, Card, InputGroup } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css';
import { schema } from '../../schema';
import { Formik, FieldArray, getIn, FormikProps, setNestedObjectValues } from 'formik';
import "../../optional"; // custom "optional" function for yup validation
import modelContext from '../../modelContext';
import { annotSpecies } from '../../types';
import { ReactionForm } from './reaction_types';
import { CustomSelect } from '../CustomFormHandling/CustomSelect';

// Reactions form is the most complicated form on the webapp. Extra data must be handled because the length of binding sites of reactants/products/modifiers here are not
// always the same as the length of the molecule when it is defined in annotation species. 

// Example: Molecule X has binding sites A, B, and C, defined in annotList
// When specifying X as a reactant, you're declaring that any molecule of X with binding site states matching whatever you declare here to be a "valid reactant". 
// you can fill any number, up to all, of the total binding sites in the reactant definiton. Any sites that are not filled are "wildcard" sites.
// If no sites were declared in reactant X, then all X molecules can be a reactant of this reaction regardless of its states in sites A, B and C.

// Here we use a series of useEffects to manage and update the relevant data.
// Part 1: reactant/product/modifier update useEffects
// As mentioned earlier, the binding site array of a reactant is a subset of the total binding sites defined in the annotList. 
// A boolean array keeps track of which of the original annot binding sites are filled by the reactant/product/modifier binding arrays.
// Example: Molecule X has binding sites A, B, and C, defined in annotList
// Array will be length 3. If the reactant binding sites have binding sites B and C filled, the array will be [False, True, True]
// These arrays are stored in a 3D array where the indices are, in order, reaction_index, moleculeComplex_index, and molecule_index. That is, each molecule has its own boolean array
// These useEffect's are triggered by the respective updateReactants/updateProducts/updateModifier variables, which trigger from changes to the reaction molecules
// List of events that trigger updateReactants/updateProducts/updateModifier: 
// 		Changing a molecule in selection dropdown, toggling active/disabled binding sites, adding/removing molecules, adding/removing molecule complexes, removing reactions 
// With this, there is a constantly maintained list that tracks the binding sites of each molecule

// Part 2: loading initial values useEffect
// With the other forms, enableReinitialize is used in the formik prop which allows the form to re-render with new values when the initialvalues provided are switched. This means that
// when a model file is loaded, we can fill in all form fields with the model data by swapping in the model data with the original dummy initialvalues object.
// Issue: there are no event listeners that let you trigger effects when the initialValues have been replaced by new values. Meaning for reactions, it is not possible to use
// the reinitialization event to trigger the useEffects in part 1. This leads to stale/outdated binding site boolean arrays that do not match the loaded data.
// The solution: For the reaction tab only, manually overwrite the initialValues object instead of using enablereinitialize. This is done using the setFormValue function
// from formik. To access formik objects outside of the formik prop, useRef<> can access them if innerRef prop is used as a formik prop. Here, we access formik functions with
// formRef?.current.values and formRef?.current.setFieldValue().
// Because the overwrite is triggered manually, I can have the binding boolean arrays be triggered right afterwards.
// Note: This useEffect is triggered by a change in the initialValues prop, so it should only trigger twice. First when the form is initially rendered (doesn't do anything), second when a file is loaded.

// Part 3: fixBindings
// Tied to the model loading useEffect, it converts the binding site arrays of reactants/products/modifiers of reactions so that they are the same length as the original annotList definition.
// This takes care of mismatched array lengths when comparing reactant binding sites with annotList binding sites. 
// Caveat: because it relies on annotlist, we generate a temporary version of the annot list to ensure that the list is up to date when doing comparisons, since there's no guarantee on which useEffects run first, and the existing list may be stale.
// Example: Molecule X has binding sites A, B, and C, defined in annotList
// Array will be length 3. If the reactant binding sites have binding sites B and C filled, the array of reactant will be [B, C]
// fixBindings converts it to [undefined, B, C] so that the indices between the two will match
// When outputting the finished model into an XML, this will need to be cleaned where undefined elements are popped, and bindingarrays with only undefined are removed.


export default function FormReactions(props: any) {
	let emptyReaction = {
		_attributes: {
			id: "",
			reversible: "false"
		},
		annotation: {
			"cell4d:reactionType": {
				"_text": ""
			},
			"cell4d:listOfCompartments": {
				"cell4d:compartment": [
					{
						_attributes: {
							id: ""
						}
					}
				]
			}
		},
		listOfReactants: {
			annotation: {
				"cell4d:speciesReference": [
					{
						"cell4d:listOfSpeciesTypes": {
							"cell4d:speciesType": []
						}
					},
				]
			},
			speciesReference: {
				_attributes: {
					species: "ignored"
				}
			}
		},
		listOfProducts: {
			annotation: {
				"cell4d:speciesReference": [
					{
						"cell4d:listOfSpeciesTypes": {
							"cell4d:speciesType": []
						}
					}
				]
			},
			speciesReference: {
				_attributes: {
					species: "ignored"
				}
			}
		},
		listOfModifiers: {
			annotation: {
				"cell4d:speciesReference": [
					{
						"cell4d:listOfSpeciesTypes": {
							"cell4d:speciesType": [
								{
									_attributes: {
										id: "empty"
									}
								}
							]
						}
					}
				]
			},
			modifierSpeciesReference: {
				_attributes: {
					"species": "ignored"
				}
			}
		},
		kineticLaw: {
			math: {
				_attributes: {
					"xmlns": "http://www.w3.org/1998/Math/MathML"
				},
				ci: {
					"_text": "Elementary Rate Law"
				}
			},
			listOfParameters: {
				parameter: [
					{
						_attributes: {
							id: "forward_const",
							name: "Kforward",
							value: "0"
						}
					},
				]
			}
		}
	}

	let interaction_kinetic_params = [
		{
			_attributes: {
				id: "forward_const",
				name: "Kforward",
				value: "0"
			}
		},
		{
			_attributes: {
				id: "reverse_const",
				name: "Kreverse",
				value: "0"
			}
		}
	]
	let enzymatic_kinetic_params = [
		{
			_attributes: {
				id: "Ka",
				name: "Ka",
				value: "0"
			}
		},
		{
			_attributes: {
				id: "Kp",
				name: "Kp",
				value: "0"
			}
		},
		{
			_attributes: {
				id: "Keq",
				name: "Keq",
				value: "0"
			}
		},
		{
			_attributes: {
				id: "Vm",
				name: "Vm",
				value: "0"
			}
		}
	]

	let empty_base_species = {
		_attributes: {
			id: ""
		},
		"cell4d:bindingSite": [],
	}
	let empty_complex = {
		"cell4d:listOfSpeciesTypes": {
			"cell4d:speciesType": [empty_base_species]
		}
	}

	let interaction_modifier_ref = [
		{
			"cell4d:listOfSpeciesTypes": {
				"cell4d:speciesType": [
					{
						_attributes: {
							id: "empty"
						}
					}
				]
			}
		}
	]

	let enzymatic_modifier_ref = [
		{
			"cell4d:listOfSpeciesTypes": {
				"cell4d:speciesType": [
					{
						_attributes: {
							id: ""
						}
					}
				]
			}
		}
	]
	// formik reference
	const formRef = useRef<FormikProps<ReactionForm>>(null);
	const model = useContext(modelContext);

	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [touchedSubmit, setTouchedSubmit] = useState(false);
	useEffect(() => { }, [submitSuccess])
	useEffect(() => { }, [touchedSubmit])


	const fixBindings = (speciesReference: any, annot_list: { [key: string]: annotSpecies }) => {
		speciesReference.forEach((complex: any, complex_index: number) => {
			complex["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].forEach((mol: any, mol_index: number) => {
				// Array length of the matching annot species' number of binding sites, True/False depending on if the reactant has that site
				if (mol?._attributes?.id && annot_list?.[mol?._attributes?.id]) {
					if (mol?.["cell4d:bindingSite"] && mol?.["cell4d:bindingSite"].length > 0) {
						let fixed_sites = Array(annot_list?.[mol?._attributes?.id].sites.length).fill(undefined);
						let site_counter = 0;
						let annot_site_list: Array<string> = annot_list[mol._attributes.id].sites.map((site) => site.id);
						annot_site_list.forEach((annot_site: any, annot_site_index: number) => {
							if (site_counter < mol["cell4d:bindingSite"].length) {
								if (annot_site_list[annot_site_index] === mol["cell4d:bindingSite"][site_counter]._attributes.id) {
									fixed_sites[annot_site_index] = mol["cell4d:bindingSite"][site_counter];
									site_counter += 1;
								}
							}
						})
						speciesReference[complex_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][mol_index]["cell4d:bindingSite"] = fixed_sites;
					}
				}
			})
		})
		return (speciesReference);
	}

	const [initialValues, setInitialValues] = useState(props.initialValues);
	// const [initialRender, setInitialRender] = useState(true);

	// setup the reaction form, this is necessary for cases where an external model is loaded in
	// deals with the wildcard binding sites of molecules in the reaction definition by triggering the useeffects after the model is loaded
	useEffect(() => {
		setInitialValues(props.initialValues)
		if (formRef?.current) {
			formRef?.current?.setFieldValue(`reaction`, props.initialValues.reaction);
			let annot_list: { [key: string]: annotSpecies } = model.writeAnnotList();
			props.initialValues.reaction.forEach((reaction: any, reaction_index: number) => {
				formRef?.current?.setFieldValue(`reaction.[${reaction_index}]["listOfReactants"]["annotation"]["cell4d:speciesReference"]`,
					fixBindings(reaction.listOfReactants.annotation["cell4d:speciesReference"], annot_list));
				formRef?.current?.setFieldValue(`reaction.[${reaction_index}]["listOfProducts"]["annotation"]["cell4d:speciesReference"]`,
					fixBindings(reaction.listOfProducts.annotation["cell4d:speciesReference"], annot_list));
				formRef?.current?.setFieldValue(`reaction.[${reaction_index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"]`,
					fixBindings(reaction.listOfModifiers.annotation["cell4d:speciesReference"], annot_list));
			})
		}
		setUpdateReactants(!updateReactants);
		setUpdateProducts(!updateProducts);
		setUpdateModifier(!updateModifier);
	}, [props.initialValues])


	// reactant update useEffect
	// Reactions: reactant_complexes: reactant_mols: binding sites
	const [reactantSites, setReactantSites] = useState(Array<Array<Array<Array<boolean>>>>());
	const [updateReactants, setUpdateReactants] = useState(false);
	useEffect(() => {
		if (formRef?.current?.values["reaction"]) {
			setReactantSites(
				formRef.current.values["reaction"].map((reaction: any, index: number) => {
					let array_of_reactant_complexes: Array<Array<Array<boolean>>> = []
					array_of_reactant_complexes = reaction.listOfReactants.annotation["cell4d:speciesReference"].map((reactant_complex: any, rIndex: number) => {
						// array where each element represents a base species, an array of the molecule's sites
						let array_of_molecules: Array<Array<boolean>> = reactant_complex["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].map((reactant_mol: any, mIndex: number) => {
							// Array length of the matching annot species' number of binding sites, True/False depending on if the reactant has that site
							let annot_bindings_array: Array<boolean> = reactant_mol?._attributes?.id && model.annotSpeciesList[reactant_mol._attributes.id] ?
								model.annotSpeciesList[reactant_mol._attributes.id].sites.map((annot_site: any, sIndex: number) => {
									let reactant_sites: Array<string> = reactant_mol["cell4d:bindingSite"].map((site: any) => {
										return site?._attributes?.id ? site._attributes.id : undefined
									})
									return (reactant_sites.includes(annot_site.id))
								})
								: []
							return (annot_bindings_array);
						})
						return (array_of_molecules);
					})
					return (array_of_reactant_complexes);
				})
			)

		}
	}, [updateReactants])

	// product update useEffect
	// Reactions: product_complexes: product_mols: binding sites
	const [productSites, setProductSites] = useState(Array<Array<Array<Array<boolean>>>>());
	const [updateProducts, setUpdateProducts] = useState(false);
	useEffect(() => {
		if (formRef?.current?.values["reaction"]) {
			setProductSites(
				formRef.current.values["reaction"].map((reaction: any, index: number) => {
					let array_of_product_complexes: Array<Array<Array<boolean>>> = []
					array_of_product_complexes = reaction.listOfProducts.annotation["cell4d:speciesReference"].map((product_complex: any) => {
						// array where each element represents a base species, an array of the molecule's sites
						let array_of_molecules: Array<Array<boolean>> = product_complex["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].map((product_mol: any, mIndex: number) => {
							// Array length of the matching annot species' number of binding sites, True/False depending on if the reactant has that site
							let annot_bindings_array: Array<boolean> = product_mol?._attributes?.id ?
								model.annotSpeciesList[product_mol._attributes.id].sites.map((annot_site: any, sIndex: number) => {
									let product_sites: Array<string> = product_mol["cell4d:bindingSite"].map((site: any) => {
										return site?._attributes?.id ? site._attributes.id : undefined
									})
									return (product_sites.includes(annot_site.id))
								})
								: []
							return (annot_bindings_array);
						})
						return (array_of_molecules);
					})
					return (array_of_product_complexes);
				})
			)

		}
	}, [updateProducts])

	// modifier update useEffect
	// Reactions: product_complexes: product_mols: binding sites
	const [modifierSites, setModifierSites] = useState(Array<Array<Array<Array<boolean>>>>());
	const [updateModifier, setUpdateModifier] = useState(false);
	useEffect(() => {
		if (formRef?.current?.values["reaction"]) {
			setModifierSites(
				formRef.current.values["reaction"].map((reaction: any, index: number) => {
					if (reaction?.annotation?.["cell4d:reactionType"]?._text !== "Enzymatic") {
						return ([[[]]])
					}
					// array of modifier complexes is just going to be length of 1, only 1 modifier particle allowed in a reaction.
					let array_of_modifier_complexes: Array<Array<Array<boolean>>> = []
					array_of_modifier_complexes = reaction.listOfModifiers.annotation["cell4d:speciesReference"].map((modifier_complex: any) => {
						// array where each element represents a base species, an array of the molecule's sites
						let array_of_molecules: Array<Array<boolean>> = modifier_complex["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].map((modifier_mol: any, mIndex: number) => {
							// Array length of the matching annot species' number of binding sites, True/False depending on if the reactant has that site
							let annot_bindings_array: Array<boolean> = modifier_mol?._attributes?.id ?
								model.annotSpeciesList[modifier_mol._attributes.id].sites.map((annot_site: any, sIndex: number) => {
									let modifier_sites: Array<string> = modifier_mol["cell4d:bindingSite"].map((site: any) => {
										return site?._attributes?.id ? site._attributes.id : undefined
									})
									return (modifier_sites.includes(annot_site.id))
								})
								: []
							return (annot_bindings_array);
						})
						return (array_of_molecules);
					})
					return (array_of_modifier_complexes);
				})
			)

		}
	}, [updateModifier])

	// helpers that decide if the compartment list array should be shown by checking its length and updating it when the length changes
	const [compartCounter, setCompartCounter] = useState(Array<number>());
	const [updateComparts, setUpdateComparts] = useState(false);
	useEffect(() => {
		if (formRef?.current?.values["reaction"]) {
			setCompartCounter(formRef?.current?.values["reaction"].map((reaction: any, index: number) => {
				if (!reaction?.annotation?.["cell4d:listOfCompartments"]?.["cell4d:compartment"]) {
					return (0)
				} else {
					return (reaction.annotation["cell4d:listOfCompartments"]["cell4d:compartment"].length)
				}
			}))
		}
	}, [JSON.stringify(model?.modelJson?.["listOfReactions"]), updateComparts])

	useEffect(() => {
		if (formRef?.current) {
			if (formRef.current.values["reaction"].length !== 0) {
				formRef.current.validateForm();
				setTimeout(() => {
					formRef?.current?.setTouched(setNestedObjectValues(formRef.current.errors, true));
				}, 100)
			}
		}
	}, [model.compartList, model.annotSpeciesList])

	const errorsMap = (error_string: string): string => {
		let return_string = "";
		if (error_string === "missing_var") {
			return_string = "This field cannot be empty.";
		} else if (error_string === "invalid_var") {
			return_string = "This field is invalid.";
		} else if (error_string === "no_match") {
			return_string = "The highlighted option is no longer valid. Please select a new option.";
		}
		return (return_string);
	}


	const handleSubmit = (values: any) => {
		let reactions_out = {
			"reaction": values["reaction"]
		}

		// because handleSubmit was successfully called, new reactions tab is correct for sure, so set it to true.
		let temp_modelStatus: Array<Boolean> = model.validateModel();
		temp_modelStatus[4] = true;
		model.setModelStatus(temp_modelStatus);

		model.appendModelJson("listOfReactions", reactions_out);
		console.log(JSON.stringify(values["reaction"]));

		setSubmitSuccess(true);
		setTouchedSubmit(false);

	}

	return (
		<div>

			<Formik
				innerRef={formRef}
				validationSchema={schema.reactions(model.compartList, Object.values(model.annotSpeciesList), model.metabList)}
				onSubmit={handleSubmit}
				enableReinitialize={false}
				initialValues={initialValues} >
				{({
					handleSubmit,
					handleChange,
					handleBlur,
					setFieldValue,
					values,
					touched,
					errors,
				}) => (
					<Form onSubmit={handleSubmit} noValidate autoComplete="off">
						{/* <pre>{JSON.stringify(showDiff, null, 2)}</pre> */}
						<Container>
							<FieldArray name="reaction" render={arrayHelpers => {
								return (<div> {
									values?.["reaction"] && values?.["reaction"].length > 0 ? values["reaction"].map((reaction: any, index: number) => (
										<div key={index}>
											<Card >
												<Row style={{ paddingTop: 10 }}>
													<Col xs={3} style={{ paddingLeft: 30 }}>
														<Form.Label>Reaction name</Form.Label>
														<Form.Control className="form-control" size="sm"
															name={`reaction.[${index}]._attributes.id`}
															value={values["reaction"][index]._attributes.id}
															onChange={handleChange}
															onBlur={handleBlur}
															isValid={getIn(touched, `reaction.[${index}]._attributes.id`) &&
																!getIn(errors, `reaction.[${index}]._attributes.id`)}
															isInvalid={getIn(touched, `reaction.[${index}]._attributes.id`) &&
																!!getIn(errors, `reaction.[${index}]._attributes.id`)} />
														<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `reaction.[${index}]._attributes.id`))}</Form.Control.Feedback>

														{/* <Button className="form-control" type="button" variant="outline-danger" onClick={() => arrayHelpers.remove(index)}>Remove reaction</Button> */}
														<br />
														<br />
													</Col>
													<Col xs={3}>
														{/* Check to see if the listOfCompartments exist. If so, render it. Otherwise, show a button that adds the list when clicked */}
														<Form.Label>Reaction compartment</Form.Label>
														{values?.["reaction"][index]?.["annotation"]?.["cell4d:listOfCompartments"] ?
															<FieldArray name={`reaction.[${index}]["annotation"]["cell4d:listOfCompartments"]["cell4d:compartment"]`} render={arrayHelpers => {
																return (<div> {
																	values?.["reaction"]?.[index]?.["annotation"]?.["cell4d:listOfCompartments"]?.["cell4d:compartment"] && values?.["reaction"]?.[index]?.["annotation"]?.["cell4d:listOfCompartments"]?.["cell4d:compartment"]?.length > 0 ?
																		values["reaction"][index]["annotation"]["cell4d:listOfCompartments"]["cell4d:compartment"].map((compart: any, cIndex: number) => (
																			<div key={cIndex}>
																				<InputGroup>
																					{/* button removes one compartment, deletes whole compartment array if it is empty */}
																					<Button type="button" size="sm" variant="outline-danger" onClick={() => {
																						arrayHelpers.remove(cIndex)
																						let new_compart_array = [...compartCounter];
																						new_compart_array[index] -= 1;
																						setCompartCounter(new_compart_array);
																						if (new_compart_array[index] === 0) {
																							setFieldValue(`reaction.[${index}]["annotation"]["cell4d:listOfCompartments"]`, undefined)
																						}
																					}}>
																						✕
																					</Button>
																					<CustomSelect
																						name={`reaction.[${index}]["annotation"]["cell4d:listOfCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].id`}
																						value={values["reaction"][index]["annotation"]["cell4d:listOfCompartments"]["cell4d:compartment"][cIndex]._attributes.id}
																						defaultOptionString="compartment"
																						touched={touched}
																						errors={errors}
																						optionsArray={model.compartList}
																						onChange={handleChange}
																						onBlur={handleBlur} />
																					<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `reaction.[${index}]["annotation"]["cell4d:listOfCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].id`))}</Form.Control.Feedback>
																				</InputGroup>
																				{/* </div> */}
																			</div>
																		)) : null
																}
																	{/* button for adding additional compartments of the reaction */}
																	<Button
																		type="button"
																		variant="outline-primary"
																		size="sm"
																		onClick={() => {
																			arrayHelpers.push(emptyReaction["annotation"]["cell4d:listOfCompartments"]["cell4d:compartment"][0])
																			let new_compart_array = [...compartCounter];
																			new_compart_array[index] += 1;
																			setCompartCounter(new_compart_array);
																		}}
																		className="justify-content-flex-start">
																		Add compartment
																	</Button>
																	<br />
																	<br />
																</div>
																)
															}}
															/>
															:
															// this button adds the array of compartments
															<Button
																// variant="warning"
																type="button"
																size="sm"
																variant="outline-primary"
																onClick={() => {
																	setFieldValue(`reaction.[${index}]["annotation"]["cell4d:listOfCompartments"]`, emptyReaction["annotation"]["cell4d:listOfCompartments"])
																	let new_compart_array = [...compartCounter];
																	new_compart_array[index] += 1;
																	setCompartCounter(new_compart_array);
																}}
																className="justify-content-flex-start">
																Add compartment
															</Button>
														}
													</Col>
													<Col xs={2} >
														<Form.Label>Reaction type</Form.Label>
														<Form.Control as="select" custom
															id="dropdown-basic-button"
															size="sm"
															name={`reaction.[${index}]["annotation"]["cell4d:reactionType"]._text`}
															value={values["reaction"][index]["annotation"]["cell4d:reactionType"]._text}
															// title={selected ? props.value : props.menuLabel}
															onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																handleChange(e);
																if (e.target.value === "Interaction") {
																	setFieldValue(`reaction.[${index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"]`,
																		interaction_modifier_ref)
																} else if (e.target.value === "Enzymatic") {
																	setFieldValue(`reaction.[${index}]._attributes.reversible`, "false");
																	setFieldValue(`reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"]`,
																		enzymatic_kinetic_params)
																	setFieldValue(`reaction.[${index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"]`,
																		enzymatic_modifier_ref)
																}
															}}
															onBlur={handleBlur}
														>
															{!values["reaction"][index]["annotation"]["cell4d:reactionType"]._text ?
																<option value="">choose option</option>
																: null}
															{["Interaction", "Enzymatic"].map((element: any, index: number) => {
																return (
																	<option value={element}>{element}</option>
																)
															})
															}
														</Form.Control>
														{/* manage reversibiliy of this reaction */}
														{values["reaction"][index]["annotation"]["cell4d:reactionType"]._text === "Interaction" ?
															<Form.Check type="checkbox" label="Reversible"
																onBlur={handleBlur}
																onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																	handleChange(e);
																	if (e.target.checked) {
																		setFieldValue(`reaction.[${index}]._attributes.reversible`, "true");
																		let first_param_name = values["reaction"][index]["kineticLaw"]["listOfParameters"]["parameter"][0]._attributes.name;
																		if (first_param_name === "Kforward" || first_param_name === "radius") {
																			setFieldValue(`reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"]`,
																				[...values["reaction"][index]["kineticLaw"]["listOfParameters"]["parameter"], interaction_kinetic_params[1]]);
																		}
																	} else {
																		setFieldValue(`reaction.[${index}]._attributes.reversible`, "false");
																		let second_param_name = values["reaction"][index]["kineticLaw"]["listOfParameters"]["parameter"]?.[1]?._attributes?.name;
																		if (second_param_name === "Kreverse") {
																			setFieldValue(`reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"]`,
																				values["reaction"][index]["kineticLaw"]["listOfParameters"]["parameter"].splice(0, 1))
																		}
																	}
																}} />
															:
															null
														}
													</Col>
													<Col xs={3}>
														{values["reaction"][index]["annotation"]["cell4d:reactionType"]._text === "Interaction" ?
															<>
																<Form.Label>Reaction rate</Form.Label>
																<Form.Control className="form-control" size="sm"
																	name={`reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`}
																	value={values["reaction"][index]["kineticLaw"]["listOfParameters"]["parameter"][0]._attributes.value}
																	onChange={handleChange}
																	onBlur={handleBlur}
																	isValid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`) &&
																		!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`)}
																	isInvalid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`) &&
																		!!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`)} />
																<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`))}</Form.Control.Feedback>
																<br />
																{values?.["reaction"][index]?._attributes?.reversible === "true" ?
																	<>
																		<Form.Label>Reverse rate</Form.Label>
																		<Form.Control className="form-control" size="sm"
																			name={`reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`}
																			value={values["reaction"][index]["kineticLaw"]["listOfParameters"]["parameter"][1]._attributes.value}
																			onChange={handleChange}
																			onBlur={handleBlur}
																			isValid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`) &&
																				!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`)}
																			isInvalid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`) &&
																				!!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`)} />
																		<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`))}</Form.Control.Feedback>
																	</>
																	:
																	null
																}
															</>
															:
															null}
														{values["reaction"][index]["annotation"]["cell4d:reactionType"]._text === "Enzymatic" ?
															<>
																<Form.Label>Michaelis-Menten rates</Form.Label>
																<InputGroup size="sm">
																	<InputGroup.Prepend>
																		<InputGroup.Text>Ka</InputGroup.Text>
																	</InputGroup.Prepend>
																	<Form.Control className="form-control"
																		name={`reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`}
																		value={values["reaction"][index]["kineticLaw"]["listOfParameters"]["parameter"][0]._attributes.value}
																		onChange={handleChange}
																		onBlur={handleBlur}
																		isValid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`) &&
																			!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`)}
																		isInvalid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`) &&
																			!!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`)} />
																	<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][0][_attributes].value`))}</Form.Control.Feedback>
																</InputGroup>
																<InputGroup size="sm">
																	<InputGroup.Prepend>
																		<InputGroup.Text>Kp</InputGroup.Text>
																	</InputGroup.Prepend>
																	<Form.Control className="form-control"
																		name={`reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`}
																		value={values["reaction"][index]["kineticLaw"]["listOfParameters"]["parameter"][1]._attributes.value}
																		onChange={handleChange}
																		onBlur={handleBlur}
																		isValid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`) &&
																			!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`)}
																		isInvalid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`) &&
																			!!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`)} />
																	<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][1][_attributes].value`))}</Form.Control.Feedback>
																</InputGroup>
																<InputGroup size="sm">
																	<InputGroup.Prepend>
																		<InputGroup.Text>Keq</InputGroup.Text>
																	</InputGroup.Prepend>
																	<Form.Control className="form-control"
																		name={`reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][2][_attributes].value`}
																		value={values["reaction"][index]["kineticLaw"]["listOfParameters"]["parameter"][2]._attributes.value}
																		onChange={handleChange}
																		onBlur={handleBlur}
																		isValid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][2][_attributes].value`) &&
																			!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][2][_attributes].value`)}
																		isInvalid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][2][_attributes].value`) &&
																			!!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][2][_attributes].value`)} />
																	<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][2][_attributes].value`))}</Form.Control.Feedback>
																</InputGroup>
																<InputGroup size="sm">
																	<InputGroup.Prepend>
																		<InputGroup.Text>Vm</InputGroup.Text>
																	</InputGroup.Prepend>
																	<Form.Control className="form-control"
																		name={`reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][3][_attributes].value`}
																		value={values["reaction"][index]["kineticLaw"]["listOfParameters"]["parameter"][3]._attributes.value}
																		onChange={handleChange}
																		onBlur={handleBlur}
																		isValid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][3][_attributes].value`) &&
																			!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][3][_attributes].value`)}
																		isInvalid={getIn(touched, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][3][_attributes].value`) &&
																			!!getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][3][_attributes].value`)} />
																	<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `reaction.[${index}]["kineticLaw"]["listOfParameters"]["parameter"][3][_attributes].value`))}</Form.Control.Feedback>
																</InputGroup>
															</>
															:
															null}
														<br />
													</Col>
												</Row>

												<Row>
													{/* List of Reactants */}
													<Col xs={6} style={{ paddingLeft: 30 }}>
														<Form.Label>List of Reactants</Form.Label>
														<FieldArray name={`reaction.[${index}]["listOfReactants"]["annotation"]["cell4d:speciesReference"]`} render={reactant_arrayHelpers => {
															return (<div> {
																values?.["reaction"]?.[index]?.["listOfReactants"]?.["annotation"]?.["cell4d:speciesReference"] && values?.["reaction"]?.[index]?.["listOfReactants"]?.["annotation"]?.["cell4d:speciesReference"].length > 0 ?
																	values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"].map((reactant_complex: any, rIndex: number) => (
																		<div key={rIndex}>
																			<Card border="light">
																				{/* <Card.Body> */}
																				<FieldArray name={`reaction.[${index}]["listOfReactants"]["annotation"]["cell4d:speciesReference"][${rIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"]`} render={base_arrayHelpers => {
																					return (<div> {
																						values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"] &&
																							values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"].length > 0 ?
																							values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].map((base_species: any, sIndex: number) => (
																								<div key={sIndex}>
																									<Row>
																										{/* Dropdown selection of base species, select name and the sites will be populated in the next column */}
																										<Col xs={5}>
																											<InputGroup size="sm">
																												<Button type="button" size="sm" variant="outline-danger" onClick={() => {
																													base_arrayHelpers.remove(sIndex)
																													setUpdateReactants(!updateReactants)
																												}}>✕</Button>
																												<Form.Control as="select" custom
																													id="dropdown-basic-button"
																													size="sm"
																													name={`reaction.[${index}]["listOfReactants"]["annotation"]["cell4d:speciesReference"][${rIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}]._attributes.id`}
																													value={values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]._attributes.id}
																													onBlur={handleBlur}
																													// reset all binding site info of base species when selection is changed
																													onChange={(e) => {
																														handleChange(e)
																														setFieldValue(`reaction.[${index}]["listOfReactants"]["annotation"]["cell4d:speciesReference"][${rIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}]["cell4d:bindingSite"]`,
																															model.annotSpeciesList[e.target.value].sites.map((site: { id: string, states: Array<string> }) => {
																																return ({
																																	_attributes: {
																																		id: site.id,
																																		state: "",
																																		binding: ""
																																	}
																																})
																															})
																														)
																														setUpdateReactants(!updateReactants)
																													}}
																												>
																													{!values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]._attributes.id ?
																														<option value="">base mol</option>
																														: null}
																													{/* allow all base species to be chosen for interactions, only allow bulk mols for enzymatic */}
																													{values["reaction"][index].annotation["cell4d:reactionType"]._text === "Interaction" ?
																														Object.keys(model.annotSpeciesList).map((annot_species: string) => {
																															return (
																																// list all annot species in the model as options
																																<option value={annot_species}>{annot_species}</option>
																															)
																														})
																														:
																														model.metabList.map((metab_name: string) => {
																															return (
																																// enzymatic reactions can only have bulk molecule reactants and products
																																// list all bulk molecule species in the model as options
																																<option value={metab_name}>{metab_name}</option>
																															)
																														})}
																												</Form.Control>
																											</InputGroup>
																										</Col>
																										{/* autopopulate list of dropdowns with the base species' binding sites */}
																										<Col xs={7}>
																											{base_species?._attributes?.id && base_species?._attributes?.id in model.annotSpeciesList && reactantSites?.[index]?.[rIndex]?.[sIndex] ?
																												model.annotSpeciesList[base_species._attributes.id].sites.map((annot_site: { id: string, states: Array<string> }, site_index: number) => {
																													// <p>{site.id}</p>
																													return (
																														<InputGroup size="sm">
																															<InputGroup.Prepend>
																																<Form.Check type="checkbox"
																																	checked={reactantSites[index][rIndex][sIndex][site_index]}
																																	onClick={(e: any) => {
																																		let current_sites = JSON.parse(JSON.stringify(values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"]));
																																		if (!e.target.checked) {
																																			current_sites[site_index] = undefined;
																																			setFieldValue(`reaction.[${index}]["listOfReactants"]["annotation"]["cell4d:speciesReference"][${rIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"]`,
																																				current_sites);
																																		} else {
																																			current_sites[site_index] = {
																																				_attributes: {
																																					id: annot_site.id,
																																					state: "",
																																					binding: ""
																																				}
																																			}
																																			setFieldValue(`reaction.[${index}]["listOfReactants"]["annotation"]["cell4d:speciesReference"][${rIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"]`,
																																				current_sites);
																																		}
																																		setUpdateReactants(!updateReactants)
																																		console.log();
																																	}} />
																																<InputGroup.Text>{annot_site.id}</InputGroup.Text>
																															</InputGroup.Prepend>
																															{/* Show dummy or real binding field depending on if it is filled in the reactant binding site */}
																															{!values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]?.["cell4d:bindingSite"]?.[site_index] ?
																																<>
																																	<Form.Control as="select" custom disabled
																																		id="dropdown-basic-button"
																																		size="sm"
																																	>
																																		<option value="">Any</option>

																																	</Form.Control>
																																</>
																																:
																																<>{annot_site.states.length > 0 ?
																																	// show this one if it is a modification site
																																	<Form.Control as="select" custom
																																		id="dropdown-basic-button"
																																		size="sm"
																																		name={`reaction.[${index}]["listOfReactants"]["annotation"]["cell4d:speciesReference"][${rIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"][${site_index}]._attributes.state`}
																																		value={values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.state}
																																		// title={selected ? props.value : props.menuLabel}
																																		onChange={handleChange}
																																		onBlur={handleBlur}
																																	>
																																		{!values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.state ?
																																			<option value="">state</option>
																																			: null}
																																		{annot_site.states.map((state: string) => {
																																			return (
																																				<option value={state}>{state}</option>
																																			)
																																		})
																																		}
																																	</Form.Control>
																																	:
																																	// show this if it is a binding site
																																	<Form.Control as="select" custom
																																		id="dropdown-basic-button"
																																		size="sm"
																																		name={`reaction.[${index}]["listOfReactants"]["annotation"]["cell4d:speciesReference"][${rIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"][${site_index}]._attributes.binding`}
																																		value={values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.binding}
																																		// title={selected ? props.value : props.menuLabel}
																																		onChange={handleChange}
																																		onBlur={handleBlur}
																																	>
																																		{!values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"][rIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.binding ?
																																			<option value="">state</option>
																																			: null}
																																		<option value={"bound"}>{"bound"}</option>
																																		<option value={"unbound"}>{"unbound"}</option>
																																	</Form.Control>
																																}</>
																															}
																														</InputGroup>
																													)
																												})
																												:
																												null}
																										</Col>
																									</Row>
																								</div>
																							)) : null
																					}
																						{/* add more base species to complex */}
																						<Col sm={6}>
																							<Button
																								type="button"
																								variant="outline-primary"
																								size="sm"
																								onClick={() => {
																									base_arrayHelpers.push(empty_base_species)
																									setUpdateReactants(!updateReactants)
																								}}
																								className="justify-content-flex-start">
																								Add base species</Button>
																							<br />
																							<br />
																						</Col>
																						{values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"].length > 1 ?
																							<Button type="button" size="sm" variant="outline-danger" onClick={() => {
																								reactant_arrayHelpers.remove(rIndex)
																								setUpdateReactants(!updateReactants)
																							}}>Remove reactant</Button>
																							: null}
																					</div>)
																				}} />
																			</Card>
																			<br />
																		</div>
																	)) : null
															}
																{/* <br /> */}
																{/* <br /> */}
																{/* button for adding additional reactant complexes */}
																{/* Only show this when there are less than 2 reactant molecule complexes. Don't allow more than 2. */}
																{values["reaction"][index]["listOfReactants"]["annotation"]["cell4d:speciesReference"].length < 2 ?
																	<Button
																		type="button"
																		variant="outline-secondary"
																		size="sm"
																		onClick={() => {
																			reactant_arrayHelpers.push(empty_complex)
																			setUpdateReactants(!updateReactants)
																		}}
																		className="justify-content-flex-start">
																		Add reactant molecule</Button>
																	: null}
															</div>
															)
														}}
														/>
													</Col>

													{/* List of Products */}
													<Col xs={6} style={{ paddingRight: 30 }}>
														<Form.Label>List of Products</Form.Label>
														<FieldArray name={`reaction.[${index}]["listOfProducts"]["annotation"]["cell4d:speciesReference"]`} render={product_arrayHelpers => {
															return (<div> {
																values?.["reaction"]?.[index]?.["listOfProducts"]?.["annotation"]?.["cell4d:speciesReference"] && values?.["reaction"]?.[index]?.["listOfProducts"]?.["annotation"]?.["cell4d:speciesReference"].length > 0 ?
																	values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"].map((product_complex: any, pIndex: number) => (
																		<div key={pIndex}>
																			<Card border="light">
																				{/* <Card.Body> */}
																				<FieldArray name={`reaction.[${index}]["listOfProducts"]["annotation"]["cell4d:speciesReference"][${pIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"]`} render={base_arrayHelpers => {
																					return (<div> {
																						values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"] &&
																							values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"].length > 0 ?
																							values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].map((base_species: any, sIndex: number) => (
																								<div key={sIndex}>
																									<Row>
																										<Col xs={5}>
																											<InputGroup size="sm">
																												<Button type="button" size="sm" variant="outline-danger" onClick={() => {
																													base_arrayHelpers.remove(sIndex);
																													setUpdateProducts(!updateProducts);
																												}}>✕</Button>
																												<Form.Control as="select" custom
																													id="dropdown-basic-button"
																													size="sm"
																													name={`reaction.[${index}]["listOfProducts"]["annotation"]["cell4d:speciesReference"][${pIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}]._attributes.id`}
																													value={values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]._attributes.id}
																													onBlur={handleBlur}
																													// reset all binding site info of base species when selection is changed
																													onChange={(e) => {
																														handleChange(e)
																														setFieldValue(`reaction.[${index}]["listOfProducts"]["annotation"]["cell4d:speciesReference"][${pIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}]["cell4d:bindingSite"]`,
																															model.annotSpeciesList[e.target.value].sites.map((site: { id: string, states: Array<string> }) => {
																																return ({
																																	_attributes: {
																																		id: site.id,
																																		state: "",
																																		binding: ""
																																	}
																																})
																															})
																														)
																														setUpdateProducts(!updateProducts);
																													}}
																												>
																													{!values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]._attributes.id ?
																														<option value="">base mol</option>
																														: null}
																													{/* allow all base species to be chosen for interactions, only allow bulk mols for enzymatic */}
																													{values["reaction"][index].annotation["cell4d:reactionType"]._text === "Interaction" ?
																														Object.keys(model.annotSpeciesList).map((annot_species: string) => {
																															return (
																																// list all annot species in the model as options
																																<option value={annot_species}>{annot_species}</option>
																															)
																														})
																														:
																														model.metabList.map((metab_name: string) => {
																															return (
																																// enzymatic reactions can only have bulk molecule reactants and products
																																// list all bulk molecule species in the model as options
																																<option value={metab_name}>{metab_name}</option>
																															)
																														})}
																												</Form.Control>
																											</InputGroup>
																										</Col>
																										<Col xs={7}>
																											{/* Display the list of dropdown menus that each controls the state of a site */}
																											{base_species?._attributes?.id && base_species?._attributes?.id in model.annotSpeciesList && productSites?.[index]?.[pIndex]?.[sIndex] ?
																												model.annotSpeciesList[base_species._attributes.id].sites.map((site: { id: string, states: Array<string> }, site_index: number) => {
																													// <p>{site.id}</p>
																													return (
																														<InputGroup size="sm">
																															<InputGroup.Prepend>
																																<Form.Check type="checkbox"
																																	checked={productSites[index][pIndex][sIndex][site_index]}
																																	onClick={(e: any) => {
																																		let current_sites = JSON.parse(JSON.stringify(values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"]));
																																		if (!e.target.checked) {
																																			current_sites[site_index] = undefined;
																																			setFieldValue(`reaction.[${index}]["listOfProducts"]["annotation"]["cell4d:speciesReference"][${pIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"]`,
																																				current_sites);
																																		} else {
																																			current_sites[site_index] = {
																																				_attributes: {
																																					id: site.id,
																																					state: "",
																																					binding: ""
																																				}
																																			}
																																			setFieldValue(`reaction.[${index}]["listOfProducts"]["annotation"]["cell4d:speciesReference"][${pIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"]`,
																																				current_sites);
																																		}
																																		setUpdateProducts(!updateProducts)
																																		console.log();
																																	}} />
																																<InputGroup.Text>{site.id}</InputGroup.Text>
																															</InputGroup.Prepend>
																															{/* If the site isn't filled out in the product binding site, show a dummy form instead */}
																															{!values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]?.["cell4d:bindingSite"]?.[site_index] ?
																																<>
																																	<Form.Control as="select" custom disabled
																																		id="dropdown-basic-button"
																																		size="sm"
																																	>
																																		<option value="">Any</option>

																																	</Form.Control>
																																</>
																																:
																																<>
																																	{site.states.length > 0 ?
																																		// show this one if it is a modification site
																																		<Form.Control as="select" custom
																																			id="dropdown-basic-button"
																																			size="sm"
																																			name={`reaction.[${index}]["listOfProducts"]["annotation"]["cell4d:speciesReference"][${pIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"][${site_index}]._attributes.state`}
																																			value={values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.state}
																																			// title={selected ? props.value : props.menuLabel}
																																			onChange={handleChange}
																																			onBlur={handleBlur}
																																		>
																																			{!values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.state ?
																																				<option value="">state</option>
																																				: null}
																																			{site.states.map((state: string) => {
																																				return (
																																					<option value={state}>{state}</option>
																																				)
																																			})
																																			}
																																		</Form.Control>
																																		:
																																		// show this if it is a binding site
																																		// <p>test state</p>
																																		<Form.Control as="select" custom
																																			id="dropdown-basic-button"
																																			size="sm"
																																			name={`reaction.[${index}]["listOfProducts"]["annotation"]["cell4d:speciesReference"][${pIndex}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"][${site_index}]._attributes.binding`}
																																			value={values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.binding}
																																			// title={selected ? props.value : props.menuLabel}
																																			onChange={handleChange}
																																			onBlur={handleBlur}
																																		>
																																			{!values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"][pIndex]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.binding ?
																																				<option value="">state</option>
																																				: null}
																																			<option value={"bound"}>{"bound"}</option>
																																			<option value={"unbound"}>{"unbound"}</option>
																																		</Form.Control>
																																	}
																																</>
																															}
																														</InputGroup>
																													)
																												})
																												:
																												null}
																										</Col>
																									</Row>
																								</div>
																							)) : null
																					}
																						{/* add more base species to complex */}
																						<Col sm={6}>
																							<Button
																								type="button"
																								variant="outline-primary"
																								size="sm"
																								onClick={() => {
																									base_arrayHelpers.push(empty_base_species);
																									setUpdateProducts(!updateProducts);
																								}}
																								className="justify-content-flex-start">
																								Add base species</Button>
																							<br />
																							<br />
																						</Col>
																						{values["reaction"][index]["listOfProducts"]["annotation"]["cell4d:speciesReference"].length > 1 ?
																							<Button type="button" size="sm" variant="outline-danger" onClick={() => {
																								product_arrayHelpers.remove(pIndex);
																								setUpdateProducts(!updateProducts);
																							}}>Remove product</Button>
																							: null}
																					</div>)
																				}} />
																			</Card>
																			<br />
																		</div>
																	)) : null
															}
																{/* <br /> */}
																{/* <br /> */}
																{/* button for adding additional reactant complexes */}
																<Button
																	type="button"
																	variant="outline-secondary"
																	size="sm"
																	onClick={() => {
																		product_arrayHelpers.push(empty_complex);
																		setUpdateProducts(!updateProducts);
																	}}
																	className="justify-content-flex-start">
																	Add product molecule</Button>
															</div>
															)
														}}
														/>
													</Col>
												</Row>
												{/* Modifier list, hidden unless enzymatic reaction */}
												{values["reaction"][index]["annotation"]["cell4d:reactionType"]._text === "Enzymatic" ?
													<Row className="justify-content-md-center">
														<Col xs={6} style={{ paddingRight: 30 }}>
															<br />
															<Form.Label>Enzyme molecule</Form.Label>
															<FieldArray name={`reaction.[${index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"]`} render={product_arrayHelpers => {
																return (<div> {
																	values?.["reaction"]?.[index]?.["listOfModifiers"]?.["annotation"]?.["cell4d:speciesReference"] && values?.["reaction"]?.[index]?.["listOfModifiers"]?.["annotation"]?.["cell4d:speciesReference"].length > 0 ?
																		values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"].map((product_complex: any, mod_index: number) => (
																			<div key={mod_index}>
																				<Card border="light">
																					{/* <Card.Body> */}
																					<FieldArray name={`reaction.[${index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][${mod_index}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"]`} render={base_arrayHelpers => {
																						return (<div> {
																							values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"] &&
																								values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"].length > 0 ?
																								values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].map((base_species: any, sIndex: number) => (
																									<div key={sIndex}>
																										<Row>
																											<Col xs={6}>
																												<InputGroup size="sm">
																													<Form.Control as="select" custom
																														id="dropdown-basic-button"
																														size="sm"
																														name={`reaction.[${index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][${mod_index}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}]._attributes.id`}
																														value={values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]._attributes.id}
																														onBlur={handleBlur}
																														// reset all binding site info of base species when selection is changed
																														onChange={(e) => {
																															handleChange(e)
																															setFieldValue(`reaction.[${index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][${mod_index}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}]["cell4d:bindingSite"]`,
																																model.annotSpeciesList[e.target.value].sites.map((site: { id: string, states: Array<string> }) => {
																																	return ({
																																		_attributes: {
																																			id: site.id,
																																			state: "",
																																			binding: ""
																																		}
																																	})
																																})
																															)
																															setUpdateModifier(!updateModifier)
																														}}
																													>
																														{!values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]._attributes.id ?
																															<option value="">base mol</option>
																															: null}
																														{/* do not allow bulk molecules to be a modifier species */}
																														{Object.keys(model.annotSpeciesList).filter((annot_species: string) => !model.metabList.includes(annot_species)).map((annot_species: string) => {
																															return (
																																// list all annot species in the model as options
																																<option value={annot_species}>{annot_species}</option>
																															)
																														})
																														}
																													</Form.Control>
																													<Button type="button" size="sm" variant="outline-danger" onClick={() => {
																														base_arrayHelpers.remove(sIndex)
																														setUpdateModifier(!updateModifier)
																													}}>✕</Button>
																												</InputGroup>
																											</Col>
																											<Col xs={6}>
																												{base_species?._attributes?.id && base_species?._attributes?.id in model.annotSpeciesList ?
																													model.annotSpeciesList[base_species._attributes.id].sites.map((site: { id: string, states: Array<string> }, site_index: number) => {
																														// <p>{site.id}</p>
																														return (
																															<InputGroup size="sm">
																																<InputGroup.Prepend>
																																	<Form.Check type="checkbox"
																																		checked={modifierSites[index][mod_index][sIndex][site_index]}
																																		onClick={(e: any) => {
																																			let current_sites = JSON.parse(JSON.stringify(values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"]));
																																			if (!e.target.checked) {
																																				current_sites[site_index] = undefined;
																																				setFieldValue(`reaction.[${index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][${mod_index}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"]`,
																																					current_sites);
																																			} else {
																																				current_sites[site_index] = {
																																					_attributes: {
																																						id: site.id,
																																						state: "",
																																						binding: ""
																																					}
																																				}
																																				setFieldValue(`reaction.[${index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][${mod_index}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"]`,
																																					current_sites);
																																			}
																																			setUpdateModifier(!updateModifier)
																																		}} />
																																	<InputGroup.Text>{site.id}</InputGroup.Text>
																																</InputGroup.Prepend>
																																{/* If the site isn't filled out in the modifier binding site, show a dummy form instead */}
																																{!values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]?.["cell4d:bindingSite"]?.[site_index] ?
																																	<>
																																		<Form.Control as="select" custom disabled
																																			id="dropdown-basic-button"
																																			size="sm"
																																		>
																																			<option value="">Any</option>

																																		</Form.Control>
																																	</>
																																	:
																																	<>
																																		{site.states.length > 0 ?
																																			// show this one if it is a modification site
																																			<Form.Control as="select" custom
																																				id="dropdown-basic-button"
																																				size="sm"
																																				name={`reaction.[${index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][${mod_index}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"][${site_index}]._attributes.state`}
																																				value={values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.state}
																																				// title={selected ? props.value : props.menuLabel}
																																				onChange={handleChange}
																																				onBlur={handleBlur}
																																			>
																																				{!values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.state ?
																																					<option value="">state</option>
																																					: null}
																																				{site.states.map((state: string) => {
																																					return (
																																						<option value={state}>{state}</option>
																																					)
																																				})
																																				}
																																			</Form.Control>
																																			:
																																			// show this if it is a binding site
																																			// <p>test state</p>
																																			<Form.Control as="select" custom
																																				id="dropdown-basic-button"
																																				size="sm"
																																				name={`reaction.[${index}]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][${mod_index}]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][${sIndex}].["cell4d:bindingSite"][${site_index}]._attributes.binding`}
																																				value={values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.binding}
																																				// title={selected ? props.value : props.menuLabel}
																																				onChange={handleChange}
																																				onBlur={handleBlur}
																																			>
																																				{!values["reaction"][index]["listOfModifiers"]["annotation"]["cell4d:speciesReference"][mod_index]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][sIndex]["cell4d:bindingSite"][site_index]._attributes.binding ?
																																					<option value="">state</option>
																																					: null}
																																				<option value={"bound"}>{"bound"}</option>
																																				<option value={"unbound"}>{"unbound"}</option>
																																			</Form.Control>
																																		}</>
																																}
																															</InputGroup>
																														)
																													})
																													:
																													null}
																											</Col>
																										</Row>
																									</div>
																								)) : null
																						}
																							{/* add more base species to complex */}
																							<Col sm={6}>
																								<Button
																									type="button"
																									variant="outline-primary"
																									size="sm"
																									onClick={() => {
																										base_arrayHelpers.push(empty_base_species)
																										setUpdateModifier(!updateModifier)
																									}}
																									className="justify-content-flex-start">
																									Add base species</Button>
																								<br />
																								<br />
																							</Col>
																						</div>)
																					}} />
																				</Card>
																				<br />
																			</div>
																		)) : null}
																</div>
																)
															}}
															/>
														</Col>
													</Row>
													: null}
												<br />
											</Card>
											<Button type="button" variant="outline-danger" onClick={() => {
												arrayHelpers.remove(index)
												setUpdateComparts(!updateComparts);
												setUpdateReactants(!updateReactants);
												setUpdateProducts(!updateProducts);
												setUpdateModifier(!updateModifier);
											}}>Remove reaction</Button>
											<br />
											<br />
										</div>
									)) : null
								}
									{/* button for adding more reactions */}
									<Button type="button" onClick={() => {
										arrayHelpers.push(emptyReaction)
										setUpdateComparts(!updateComparts);
										setUpdateReactants(!updateReactants);
										setUpdateProducts(!updateProducts);
										setUpdateModifier(!updateModifier);
									}}>Add new reaction</Button>
								</div>
								)
							}}
							/>
							{/* <br /> */}
							<hr />

							<Button type="submit" onClick={() => setSubmitSuccess(false)}>Save Reactions to model</Button>
							{submitSuccess ? <><br /><br /><div className="alert-success col-lg justify-content-md-center">Reactions saved to model!</div></> : 
											 touchedSubmit ? <><br /><br /><div className="alert-danger col-lg justify-content-md-center">Reactions failed to save.</div></> :
											 null}

							{/* <Row className="text-left">
								<Col>
									Values
									<pre>{JSON.stringify(values, null, 2)}</pre>
								</Col>
								<Col>
									Errors
									<pre>{JSON.stringify(errors, null, 2)}</pre>
								</Col>
							</Row> */}
						</Container>
					</Form>
				)
				}
			</Formik>
		</div >
	);
}
